import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import { AGE_GROUPS } from "@/lib/tracks";
import GenderField from "@/components/GenderField";
import { formatDisplayName } from "@/lib/names";

function DupesView({ dupes, busy, onBack, onAddAnyway, onUseExisting }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="dir-add-dupes">
      <div className="w-full max-w-md card-surface p-6" style={{ background: "var(--bg-alt)" }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Possible duplicate</h3>
          <button onClick={onBack} className="btn-ghost text-xs"><X size={16} /></button>
        </div>
        <p className="text-sm mb-3" style={{ color: "var(--ink-2)" }}>
          We already have {dupes.length === 1 ? "this person" : "these people"} in the directory. Use the existing profile or add anyway?
        </p>
        <div className="space-y-2 mb-4 max-h-52 overflow-y-auto">
          {dupes.map((d) => (
            <div key={d.id} className="p-3 rounded-md text-sm flex items-center justify-between gap-2" style={{ background: "var(--surface-hover)", border: "1px solid var(--border-subtle)" }} data-testid={`dir-add-dupe-${d.id}`}>
              <div className="min-w-0">
                <div className="font-medium" style={{ color: "var(--ink)" }}>{formatDisplayName(d)}</div>
                <div className="text-[0.65rem]" style={{ color: "var(--muted)" }}>{[d.phone, d.email, d.lifecycle_status].filter(Boolean).join(" · ")}</div>
              </div>
              {onUseExisting && (
                <button onClick={() => onUseExisting(d)} className="btn-ghost text-xs shrink-0" style={{ padding: "0.3rem 0.7rem" }} data-testid={`dir-add-dupe-use-${d.id}`}>Use this person</button>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={onBack} className="btn-ghost flex-1" data-testid="dir-add-dupe-back">Back to form</button>
          <button onClick={onAddAnyway} disabled={busy} className="btn-primary flex-1" data-testid="dir-add-dupe-continue">Add anyway</button>
        </div>
      </div>
    </div>
  );
}

export default function AddDirectoryPersonDialog({ orgId, onClose, onCreated, onUseExisting, defaultLifecycle = "member", initialName = "" }) {
  const [first, ...rest] = initialName.trim().split(" ");
  const [form, setForm] = useState({ first_name: first || "", last_name: rest.join(" "), gender: null, phone: "", email: "", age_group: "", lifecycle_status: defaultLifecycle });
  const [busy, setBusy] = useState(false);
  const [dupes, setDupes] = useState(null);

  const doSubmit = async (force = false) => {
    if (!form.first_name.trim()) { toast.error("First name is required."); return; }
    setBusy(true);
    try {
      const r = await api.post("/coord/guests", form, { params: { org_id: orgId, force } });
      toast.success("Added to the directory.");
      onCreated(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d && d.code === "duplicate_guest") setDupes(d.duplicates || []);
      else toast.error(typeof d === "string" ? d : "Couldn't add.");
    } finally { setBusy(false); }
  };

  if (dupes) {
    return <DupesView dupes={dupes} busy={busy} onBack={() => setDupes(null)} onAddAnyway={() => { setDupes(null); doSubmit(true); }} onUseExisting={onUseExisting} />;
  }

  const input = (k, placeholder, extra = "") => (
    <input placeholder={placeholder} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`px-3 py-2 text-sm ${extra}`} data-testid={`dir-add-${k.replace("_name", "")}`} />
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="dir-add-dialog">
      <div className="w-full max-w-md card-surface p-6" style={{ background: "var(--bg-alt)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Add to directory</h3>
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="dir-add-close"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {input("first_name", "First name *")}
            {input("last_name", "Last name")}
          </div>
          {input("phone", "Phone", "w-full")}
          {input("email", "Email", "w-full")}
          <select value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="dir-add-age">
            <option value="">Age group…</option>
            {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <GenderField value={form.gender} onChange={(g) => setForm({ ...form, gender: g })} testid="dir-add-gender" />
          <label className="block">
            <span className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Category</span>
            <select value={form.lifecycle_status} onChange={(e) => setForm({ ...form, lifecycle_status: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="dir-add-lifecycle">
              <option value="guest">Guest — being evangelized (First Steps)</option>
              <option value="disciple">Disciple — in Next Steps</option>
              <option value="member">Member — church body</option>
            </select>
          </label>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button onClick={() => doSubmit(false)} disabled={busy || !form.first_name.trim()} className="btn-primary flex-1" data-testid="dir-add-submit">
            {busy ? "Adding…" : "Add person"}
          </button>
        </div>
      </div>
    </div>
  );
}
